import { getTacAdjustment, isTacPercentKey, resolveMarketTac, type TacResolution, type TacPrintSummary } from '../../utils/tac';
import { extractVariableKeys, resolveConditionalTextSource, valueForKeyPath } from '../textFormatter';
import { evaluateTemplate, prepareRenderData, type PrepareRenderDataOptions } from '../templateRuntime';
import { buildConditionTypeByKey } from '../conditions';
import type { Template } from '../types';
import { createFormatTextContent } from './pdfGenerator';

export type TacPrintOutput = {
  kind: 'percent' | 'text';
  values: string[];
  outputs: Array<TacResolution & { elementId: string; values: string[] }>;
  provisional?: boolean;
  summary: TacPrintSummary;
};
const labelKeys = new Set(['qrValue', 'qrIndex', 'labelIndex']);

export function resolveTacPrintOutput(
  options: Omit<PrepareRenderDataOptions, 'template'> & {
    template: Template;
    hasCoa: boolean;
    labelCount?: number;
    qrValues?: readonly string[];
    qrIndexOffset?: number;
    qrValuesPending?: boolean;
  },
): TacPrintOutput {
  const { template, hasCoa } = options;
  const { fullData, themeData, rawDataContext, templateFnData } = prepareRenderData(options);
  let tacIndependentFunctionData: Record<string, unknown> = {};
  if (template.templateFn && rawDataContext.coa) {
    const ingredients = rawDataContext.coa.ingredients ?? {};
    const totalActiveCannabinoids = ingredients.totalActiveCannabinoids ?? {};
    tacIndependentFunctionData = evaluateTemplate(template, {
      coa: {
        ...rawDataContext.coa,
        ingredients: {
          ...ingredients,
          totalActiveCannabinoids: { ...totalActiveCannabinoids, percent: 1_000_000_000.12345 },
        },
      },
      job: rawDataContext.job,
      product: rawDataContext.product,
      theme: themeData,
      assets: options.assets,
    });
  }
  const functionOutputDependsOnTac = (key: string) => {
    const rootKey = key.split(/[.[\]]/, 1)[0];
    const reportedFunctionData = rawDataContext.tacReportedFunctionData;
    const functionData = reportedFunctionData
      && Object.prototype.hasOwnProperty.call(reportedFunctionData, rootKey)
      ? reportedFunctionData : templateFnData;
    if (!Object.prototype.hasOwnProperty.call(functionData, rootKey)) return false;
    return JSON.stringify(valueForKeyPath(key, functionData))
      !== JSON.stringify(valueForKeyPath(key, tacIndependentFunctionData));
  };
  const formatText = createFormatTextContent({
    data: fullData, rawDataContext, hasCoa, isPreview: false,
    themeVariables: template.themeVariables,
    conditionTypeByKey: buildConditionTypeByKey(template.themeVariables),
  });
  // Function outputs need not retain TAC's variable identity. Audit actual label
  // text for function-backed templates instead of guessing which numbers are TAC.
  const hasFunctionOutput = [template.templateFn, template.labelFn]
    .some(fn => typeof fn === 'string' && fn.length > 0);
  const kind = hasFunctionOutput ? 'text' : 'percent';
  const elements = template.elements.filter(element => element.type === 'text');
  const referencedKeys = elements.flatMap(element => [
    element.variableKey, ...extractVariableKeys(element.text),
    ...(element.conditions?.flatMap(condition => [condition.variableKey, ...extractVariableKeys(condition.text ?? '')]) ?? []),
  ]);
  const usesLabelData = referencedKeys.some(key => key !== undefined && labelKeys.has(key));
  const provisional = options.qrValuesPending === true && referencedKeys.includes('qrValue');
  const labelCount = usesLabelData ? options.labelCount ?? 1 : 1;
  const qrValues = options.qrValues ?? [];
  const qrElements = template.elements.filter(element => element.type === 'qr');
  const values = new Set<string>();
  const outputs = new Map<string, {
    output: TacPrintOutput['outputs'][number];
    seenValues: Set<string>;
  }>();
  const summary: TacPrintSummary = { calculated: 0, unavailable: 0 };
  for (let index = 0; index < labelCount; index++) {
    const qrIndex = index + (options.qrIndexOffset ?? 0);
    const labelData = {
      ...fullData,
      ...themeData,
      qrIndex,
      labelIndex: template.outputFormat === 'zpl' ? index : qrIndex,
      qrValue: provisional ? '[QR value assigned when run starts]'
        : qrValues.length ? qrValues[qrIndex] ?? qrValues[qrValues.length - 1]
          : template.outputFormat === 'zpl' ? '' : qrElements[0]?.data || `QR-${qrIndex}`,
    };
    for (const element of elements) {
      // Before issuance, conservatively include each QR-dependent branch while
      // retaining the priority of preceding conditions whose inputs are known.
      const candidates = provisional ? [
        ...(element.conditions?.flatMap((condition, conditionIndex) => condition.variableKey === 'qrValue' ? [{
          ...element,
          text: resolveConditionalTextSource(condition.text, element.text, element.variableKey),
          conditions: element.conditions?.slice(0, conditionIndex).filter(previous => previous.variableKey !== 'qrValue'),
        }] : []) ?? []),
        { ...element, conditions: element.conditions?.filter(condition => condition.variableKey !== 'qrValue') },
      ] : [element];
      for (const candidate of candidates) {
        let source = '';
        const text = formatText(candidate, labelData, resolved => { source = resolved; });
        const sourceKeys = extractVariableKeys(source);
        const key = sourceKeys.find(isTacPercentKey);
        const enabled = candidate.tacCalculationEnabled === true;
        const tacDependentFunctionOutput = kind === 'text'
          && !enabled
          && sourceKeys.some(functionOutputDependsOnTac);
        const tacBearingOutput = Boolean(key || tacDependentFunctionOutput
          || (kind === 'text' && enabled && sourceKeys.length > 0));
        const tacIndependentStaticOutput = enabled && sourceKeys.length === 0;
        const value = kind === 'text' ? text : key
          ? formatText({ ...candidate, text: `{{${key}}}`, conditions: undefined }, labelData) : '';
        const resolvedTac = resolveMarketTac(options.coa, enabled ? getTacAdjustment(template, options.coa) : undefined, enabled);
        const resolution: TacResolution = resolvedTac.value === null
          ? { ...resolvedTac, provenance: 'unavailable' } : resolvedTac;
        if (tacBearingOutput) {
          if (resolution.provenance === 'calculated' || resolution.provenance === 'edited') summary.calculated = 1;
          if (resolution.provenance === 'unavailable') summary.unavailable = 1;
        }
        if (!tacIndependentStaticOutput && value.trim()) values.add(value);
        if (tacBearingOutput && value.trim()) {
          let entry = outputs.get(element.id);
          if (!entry) {
            entry = { output: { ...resolution, elementId: element.id, values: [] }, seenValues: new Set() };
            outputs.set(element.id, entry);
          }
          if (!entry.seenValues.has(value)) {
            entry.seenValues.add(value);
            entry.output.values.push(value);
          }
        }
      }
    }
  }
  return {
    kind, values: Array.from(values), outputs: Array.from(outputs.values(), entry => entry.output), summary,
    ...(provisional ? { provisional: true } : {}),
  };
}
